import { getHours, getMinutes, getSeconds, parseISO } from 'date-fns';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import {
  questExpirationDateSelector,
  selectConfigurationParameterById,
} from '!core/redux';
import { accountBalanceSelectedSelector } from '!core/redux/accountBalance/accountBalance.selectors';
import { securityAccountSelectedInfoSelector } from '!core/redux/userSecuritiesAccounts/userSecuritiesAccounts.selectors';
import TrlGridCellTrlPillAnulPending from '»components/atoms/TrlPills/TrlPillAnulPending';
import TrlGridCellTrlPillCanceled from '»components/atoms/TrlPills/TrlPillCanceled';
import TrlGridCellTrlPillClean from '»components/atoms/TrlPills/TrlPillClean';
import TrlGridCellTrlPillExecuted from '»components/atoms/TrlPills/TrlPillExecuted';
import TrlGridCellTrlPillExpired from '»components/atoms/TrlPills/TrlPillExpired';
import TrlGridCellTrlPillParcExec from '»components/atoms/TrlPills/TrlPillParcExec';
import TrlGridCellTrlPillPending from '»components/atoms/TrlPills/TrlPillPending';
import TrlGridCellTrlPillRejected from '»components/atoms/TrlPills/TrlPillRejected';
import { isTodayAfterGivenDate } from '»utils/date';
import { getDateTimeFromTimeZone } from '»utils/time';
import {
  TrlFieldDefaultValue,
  trlFieldDefaultValue,
} from '»utils/trlFieldDefaultValue';

import { useCurrentPage } from './useCurrentPage';

export const useOrdStatusLabel = () => {
  const intl = useIntl();

  const getOrdStatusLabel = (ordStatus, type) => {
    const status = ordStatus?.toUpperCase();

    switch (status) {
      case '4':
        if (type === 'pill') return TrlGridCellTrlPillCanceled;
        return intl.formatMessage({ id: 'TRLT_UI-CANCELLED' });
      case 'A':
      case 'H':
      case 'S':
        if (type === 'pill') return TrlGridCellTrlPillPending;
        return intl.formatMessage({ id: 'TRLT_UI-PENDING' });
      case 'C':
        if (type === 'pill') return TrlGridCellTrlPillExpired;
        return intl.formatMessage({ id: 'TRLT_UI-EXPIRED' });
      case '6':
      case 'T':
        if (type === 'pill') return TrlGridCellTrlPillAnulPending;
        return intl.formatMessage({ id: 'TRLT_UI-PENDING_CANCELLATION' });
      case '0':
        if (type === 'pill') return TrlGridCellTrlPillClean;
        return intl.formatMessage({ id: 'TRLT_UI-EXCHANGE' });
      case '2':
      case '3':
        if (type === 'pill') return TrlGridCellTrlPillExecuted;
        return intl.formatMessage({ id: 'TRLT_UI-EXECUTED' });
      case '1':
        if (type === 'pill') return TrlGridCellTrlPillParcExec;
        return intl.formatMessage({ id: 'TRLT_UI-PART_EXEC' });
      case '8':
        if (type === 'pill') return TrlGridCellTrlPillRejected;
        return intl.formatMessage({ id: 'TRLT_UI-REJECTED' });
      default:
        if (type === 'pill') return TrlFieldDefaultValue;
        return trlFieldDefaultValue();
    }
  };

  return { getOrdStatusLabel };
};

export const useTrancheTypeLabel = () => {
  const intl = useIntl();

  const getTrancheTypeLabel = (trancheType) => {
    switch (trancheType) {
      case 'B':
        return intl.formatMessage({ id: 'TRLT_UI-BOOKBUILDING' });
      case 'R':
        return intl.formatMessage({ id: 'TRLT_UI-RETAIL' });
      default:
        return trlFieldDefaultValue();
    }
  };

  return { getTrancheTypeLabel };
};

const toSecondsSinceMidnight = (date) =>
  getHours(date) * 3600 + getMinutes(date) * 60 + getSeconds(date);

const isoTimeToSeconds = (iso) => toSecondsSinceMidnight(parseISO(iso));

const isMarketOpened = ({ timeZone, compareMode, startDate, endDate }) => {
  const bankDateTime = getDateTimeFromTimeZone({
    timeZone,
    noTime: compareMode === 'date',
  });

  if (compareMode === 'date' || compareMode === 'dateTime')
    return (
      bankDateTime >= new Date(startDate) &&
      bankDateTime <= new Date(endDate ?? 8.64e15)
    );

  const nowSec = toSecondsSinceMidnight(bankDateTime);
  const startSec = isoTimeToSeconds(startDate);
  const endSec = isoTimeToSeconds(endDate);

  return nowSec >= startSec && nowSec <= endSec;
};

export const useIsTitleNegotiable = ({
  isMarketPrimaryScreen,
  isFundsPrimaryScreen,
  canTrade,
  endDate,
  startDate,
}) => {
  const isPrimaryScreen = isMarketPrimaryScreen || isFundsPrimaryScreen;
  const currentSecurityAccount = useSelector(
    securityAccountSelectedInfoSelector,
  );
  const currentBalanceAccount = useSelector(accountBalanceSelectedSelector);
  const questExpDate = useSelector(questExpirationDateSelector);
  const timeZone = useSelector((state) =>
    selectConfigurationParameterById(state, 'TIME_ZONE'),
  ).value;

  if (!currentBalanceAccount) return false;

  if (questExpDate && isTodayAfterGivenDate(questExpDate)) return false;

  const isInstrumentTradable =
    currentSecurityAccount.canTrade === 'Y' &&
    (isPrimaryScreen ? true : canTrade === 'Y');

  let isInTradingInterval = true;

  if (startDate !== undefined && endDate !== undefined)
    isInTradingInterval = isMarketOpened({
      timeZone,
      compareMode: isFundsPrimaryScreen ? 'date' : 'dateTime',
      startDate,
      endDate,
    });

  return isInTradingInterval && isInstrumentTradable;
};

export const useIsOrderEditable = ({
  revokeDate,
  ordStatus,
  securityType,
  side,
  localStartTime,
  localEndTime,
}) => {
  const currentBalanceAccount = useSelector(accountBalanceSelectedSelector);
  const questExpDate = useSelector(questExpirationDateSelector);
  const timeZone = useSelector((state) =>
    selectConfigurationParameterById(state, 'TIME_ZONE'),
  ).value;
  const { isMarketStockExchangeOrders } = useCurrentPage();

  const bankDate = getDateTimeFromTimeZone({ timeZone, noTime: true });

  if (!currentBalanceAccount) return false;

  if (
    (questExpDate && isTodayAfterGivenDate(questExpDate)) ||
    (securityType === 'FUND' && (side === 'E' || side === 'D'))
  )
    return false;

  let isInTradingInterval = true;

  if (isMarketStockExchangeOrders && !!localStartTime && !!localEndTime)
    isInTradingInterval = isMarketOpened({
      timeZone,
      compareMode: 'time',
      startDate: localStartTime,
      endDate: localEndTime,
    });

  let afterRevokeDate = false;

  if (revokeDate) afterRevokeDate = new Date(revokeDate) <= bankDate;

  return (
    isInTradingInterval &&
    !afterRevokeDate &&
    (ordStatus === '0' ||
      (isMarketStockExchangeOrders ? ordStatus === '1' : false))
  );
};

export const useIsOrderCancellable = ({ revokeDate, ordStatus }) => {
  const questExpDate = useSelector(questExpirationDateSelector);
  const timeZone = useSelector((state) =>
    selectConfigurationParameterById(state, 'TIME_ZONE'),
  ).value;

  const bankDateTime = getDateTimeFromTimeZone({ timeZone, noTime: true });

  if (questExpDate && isTodayAfterGivenDate(questExpDate)) return false;

  let afterRevokeDate = false;

  const ordStatusUppercased = ordStatus?.toUpperCase();

  if (revokeDate) afterRevokeDate = new Date(revokeDate) <= bankDateTime;

  return (
    !afterRevokeDate &&
    (ordStatusUppercased === '0' ||
      ordStatusUppercased === 'H' ||
      ordStatusUppercased === '1' ||
      ordStatusUppercased === 'S')
  );
};
